import { createHash, createHmac, randomBytes } from 'crypto';
import { DomainError } from '../errors/domain-error';
import { assertPublicHttpHostname } from '../security/url-security.policy';

/** Stable public API / webhook contract version (Marketing S22 foundation). */
export const MARKETING_PUBLIC_API_VERSION = '2026-08-13';

export const MARKETING_API_KEY_PREFIX = 'lmk_live_';

export const MARKETING_WEBHOOK_SECRET_PREFIX = 'whsec_';

export const MARKETING_DEVELOPER_API_KEY_MAX = 5;

export const MARKETING_WEBHOOK_ENDPOINT_MAX = 3;

export const MARKETING_WEBHOOK_DELIVERY_QUEUE = 'marketing-webhook-delivery';

export const MARKETING_WEBHOOK_EVENT_TYPES = [
  'subscriber.confirmed',
  'subscriber.unsubscribed',
] as const;

export type MarketingWebhookEventType = (typeof MARKETING_WEBHOOK_EVENT_TYPES)[number];

export const MARKETING_API_KEY_SCOPES = ['subscribers:read'] as const;

export type MarketingApiKeyScope = (typeof MARKETING_API_KEY_SCOPES)[number];

export type MarketingWebhookDeliveryJob = {
  deliveryId: string;
};

export type MarketingWebhookEnvelope<T extends Record<string, unknown>> = {
  id: string;
  type: MarketingWebhookEventType;
  apiVersion: typeof MARKETING_PUBLIC_API_VERSION;
  createdAt: string;
  data: T;
};

export type MarketingWebhookSubscriberPayload = {
  publicationId: string;
  publicationSlug: string | null;
  contactId: string;
  email: string;
  status: 'confirmed' | 'unsubscribed';
  occurredAt: string;
};

export function hashMarketingApiKey(rawKey: string, pepper?: string | null): string {
  const trimmed = rawKey.trim();
  const secret = pepper?.trim();
  if (secret) {
    return createHmac('sha256', secret).update(trimmed).digest('hex');
  }
  return createHash('sha256').update(trimmed).digest('hex');
}

export function marketingApiKeyHashCandidates(rawKey: string, pepper?: string | null): string[] {
  const trimmed = rawKey.trim();
  const secret = pepper?.trim();
  if (!secret) return [hashMarketingApiKey(trimmed)];
  const peppered = hashMarketingApiKey(trimmed, secret);
  const legacy = hashMarketingApiKey(trimmed);
  return peppered === legacy ? [peppered] : [peppered, legacy];
}

export function generateMarketingApiKeyMaterial(pepper?: string | null): {
  rawKey: string;
  prefix: string;
  keyHash: string;
} {
  const suffix = randomBytes(24).toString('base64url');
  const rawKey = `${MARKETING_API_KEY_PREFIX}${suffix}`;
  return {
    rawKey,
    prefix: rawKey.slice(0, 16),
    keyHash: hashMarketingApiKey(rawKey, pepper),
  };
}

export function generateMarketingWebhookSecret(): string {
  return `${MARKETING_WEBHOOK_SECRET_PREFIX}${randomBytes(24).toString('base64url')}`;
}

export function assertMarketingWebhookUrl(raw: string): string {
  const url = raw.trim();
  if (!url) throw new DomainError('Webhook URL is required');
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new DomainError('Webhook URL must be valid');
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new DomainError('Webhook URL must use http or https');
  }
  const normalizedHost = parsed.hostname.replace(/^\[/, '').replace(/\]$/, '').toLowerCase();
  const allowLocalhost =
    parsed.protocol === 'http:' &&
    (normalizedHost === 'localhost' || normalizedHost === '127.0.0.1' || normalizedHost === '::1');
  if (parsed.protocol === 'http:' && !allowLocalhost) {
    throw new DomainError('Non-local webhook URLs must use https');
  }
  try {
    assertPublicHttpHostname(parsed.hostname, { allowLocalhost });
  } catch (error) {
    throw new DomainError(
      error instanceof Error ? error.message : 'Webhook URL hostname is not allowed',
    );
  }
  return parsed.toString();
}

export function normalizeMarketingWebhookEvents(events: unknown): MarketingWebhookEventType[] {
  if (!Array.isArray(events) || events.length === 0) {
    return [...MARKETING_WEBHOOK_EVENT_TYPES];
  }
  const allowed = new Set<string>(MARKETING_WEBHOOK_EVENT_TYPES);
  const normalized = events
    .map((value) => String(value).trim())
    .filter((value) => allowed.has(value)) as MarketingWebhookEventType[];
  if (normalized.length === 0) {
    throw new DomainError('Select at least one webhook event');
  }
  return normalized;
}

export function assertMarketingApiKeyScope(scope: string): MarketingApiKeyScope {
  if (!(MARKETING_API_KEY_SCOPES as readonly string[]).includes(scope)) {
    throw new DomainError(`Unsupported API key scope: ${scope}`);
  }
  return scope as MarketingApiKeyScope;
}

export function buildMarketingWebhookEnvelope<T extends Record<string, unknown>>(
  type: MarketingWebhookEventType,
  data: T,
  eventId?: string,
): MarketingWebhookEnvelope<T> {
  return {
    id: eventId ?? `evt_${randomBytes(12).toString('hex')}`,
    type,
    apiVersion: MARKETING_PUBLIC_API_VERSION,
    createdAt: new Date().toISOString(),
    data,
  };
}

export function signMarketingWebhookPayload(secret: string, timestamp: number, body: string): string {
  return createHmac('sha256', secret).update(`${timestamp}.${body}`).digest('hex');
}

export function marketingWebhookSignatureHeader(secret: string, body: string, timestamp = Date.now()): string {
  const signature = signMarketingWebhookPayload(secret, timestamp, body);
  return `t=${timestamp},v1=${signature}`;
}

export const MARKETING_DEVELOPER_FOUNDATION_COPY =
  'Marketing S22 is the first creator-scoped public API + outbound webhook foundation. Platform-wide developer portal unification is backlog.';
